import { useLayoutEffect, useRef, useState } from 'react';

/** Geometry of a one-line colour swatch row, in px. */
export interface SwatchRowGeometry {
  swatchSize: number;
  gap: number;
  /** Width the expand toggle takes, including the space before it. */
  toggleWidth: number;
}

/**
 * How many swatches the collapsed row shows: all of them when they fit,
 * otherwise as many as fit beside the expand toggle, and never fewer than one.
 */
export function visibleSwatchCount(
  availableWidth: number,
  total: number,
  { swatchSize, gap, toggleWidth }: SwatchRowGeometry
): number {
  const fit = (width: number) => Math.floor((width + gap) / (swatchSize + gap));
  if (fit(availableWidth) >= total) return total;
  return Math.max(1, fit(availableWidth - toggleWidth));
}

/**
 * Measures a swatch row and returns its collapsed capacity, or null until the
 * row has been laid out (jsdom never lays out, so tests without a stub keep
 * the caller's fallback).
 *
 * `availableWidth` turns the row element into the width left for swatches,
 * e.g. minus a label in front of them.
 */
export function useSwatchCapacity(
  row: HTMLElement | null,
  total: number,
  geometry: SwatchRowGeometry,
  availableWidth: (row: HTMLElement) => number = (el) => el.clientWidth
): number | null {
  const [capacity, setCapacity] = useState<number | null>(null);
  // Read through a ref: callers pass inline functions and geometry objects,
  // which would otherwise re-subscribe the observer on every render.
  const latest = useRef({ geometry, availableWidth });
  latest.current = { geometry, availableWidth };
  const { swatchSize, gap, toggleWidth } = geometry;

  useLayoutEffect(() => {
    if (!row || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      if (row.clientWidth === 0) return; // not laid out yet
      const { geometry: g, availableWidth: width } = latest.current;
      setCapacity(visibleSwatchCount(width(row), total, g));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [row, total, swatchSize, gap, toggleWidth]);

  return capacity;
}
